import { Template } from "@xapp/ovai-lib";
import { isAdmin } from "auth";
import { UserProfile } from "auth/modules/state";
import { FormFields, NewAppForm } from "components/App";
import { FavoritesApps, FavoriteApp, getFavoritesApps } from "components/FavoritesApps";
import { getRecentlyVisitedApps, RecentlyVisitedApp, RecentlyVisitedApps } from "components/RecentlyVisitedApps";
import { Organization } from "generated/graphql";
import { ClientApp } from "models";
import React, { PureComponent } from "react";
import { Button, IconButton } from "react-toolbox/lib/button";
import { Dialog } from "react-toolbox/lib/dialog";
import { Drawer } from "react-toolbox/lib/drawer";
import { List, ListItem } from "react-toolbox/lib/list";
import darkButton from "styles/overrides/button-dark.scss";
import { getAvailableTemplateTypes } from "utils/account";
import { AdminMenu } from "./AdminMenu";
import style from "./style.scss";
import theme from "./theme.scss";
import { UserMenu } from "./UserMenu";

export const STATUS_URL = window.xaStudioConfig?.statusPageUrl || "https://status.xapp.ai"; //__STATUS_PAGE_URL__
export const SUPPORT_URL = window.xaStudioConfig?.documentationUrl || "https://documentation.xapp.ai";

export interface NavDrawerDispatchProps {
    onChangePerspective(asUser: boolean): void;
    onCreateApp(app: ClientApp): void;
    onCreateTemplate(template: Template): void;
    onExternalClick(url: string): void;
    onNavClick(path: string): void;
}
export interface NavDrawerOwnProps {
    organizations: Pick<Organization, "organizationId" | "name">[];
    isActive: boolean;
    onToggleClick(): void;
}
export interface NavDrawerStateProps {
    asUser?: boolean;
    isCreatingApp?: boolean;
    profile: UserProfile;
}
interface NavDrawerState {
    isCreatingApp: boolean;
    favoritesApps: FavoriteApp[]
    recentlyVisitedApps: RecentlyVisitedApp[];
    showCreateAppDialog: boolean;
}
export type NavDrawerProps = NavDrawerDispatchProps & NavDrawerOwnProps & NavDrawerStateProps;

export class NavDrawer extends PureComponent<NavDrawerProps, NavDrawerState> {
    constructor(props: NavDrawerProps) {
        super(props);

        this.state = {
            isCreatingApp: false,
            favoritesApps: getFavoritesApps(),
            recentlyVisitedApps: getRecentlyVisitedApps(),
            showCreateAppDialog: false
        };

        this.handleCancelClick = this.handleCancelClick.bind(this);
        this.handleNewAppClick = this.handleNewAppClick.bind(this);
        this.handleNavClick = this.handleNavClick.bind(this);
        this.handleSaveClick = this.handleSaveClick.bind(this);
        this.handleStatusClick = this.handleStatusClick.bind(this);
        this.handleSupportClick = this.handleSupportClick.bind(this);
    }

    componentWillReceiveProps(newProps: NavDrawerProps) {
        let { showCreateAppDialog, favoritesApps, recentlyVisitedApps } = this.state;

        // if the app is moving from isCreating => !isCreating
        if (this.state.isCreatingApp && !newProps.isCreatingApp) {
            // the process failed or the app has been created
            // either way, dismiss the dialog
            showCreateAppDialog = false;
        }

        // if the drawer is being opened
        if (newProps.isActive) {
            favoritesApps = getFavoritesApps();
            recentlyVisitedApps = getRecentlyVisitedApps();
        }

        // refresh the recently edited apps
        this.setState({
            isCreatingApp: newProps.isCreatingApp,
            favoritesApps,
            recentlyVisitedApps,
            showCreateAppDialog
        });
    }

    handleCancelClick() {
        this.setState({
            showCreateAppDialog: false
        });
    }

    handleNavClick(path: string) {
        this.props.onToggleClick();
        this.props.onNavClick(path);
    }

    handleNewAppClick() {
        this.setState(
            {
                showCreateAppDialog: true
            },
            this.props.onToggleClick
        );
    }

    handleSaveClick(app: ClientApp) {
        if (app.templateType === "CUSTOM") {
            return this.props.onCreateApp(app);
        } else {
            this.props.onCreateTemplate(app as Template);
        }
    }

    handleStatusClick() {
        this.props.onExternalClick(STATUS_URL);
    }

    handleSupportClick() {
        this.props.onExternalClick(SUPPORT_URL);
    }

    renderNewAppDialog() {
        const { organizations } = this.props;

        const formFields: FormFields = {
            appId: {
                show: false
            },
            organizationId: {
                disabled: true,
                show: true,
                value: ""
            },
            templateType: {
                disabled: false,
                show: true,
                value: undefined
            }
        };

        if (organizations && organizations.length === 1) {
            formFields.organizationId = {
                disabled: true,
                show: false,
                value: organizations[0].organizationId
            };
        }

        return (
            <Dialog active={this.state.showCreateAppDialog}>
                <NewAppForm
                    defaultFieldValues={formFields}
                    availableTemplates={getAvailableTemplateTypes()}
                    isBusy={this.props.isCreatingApp}
                    organizations={organizations}
                    onCancel={this.handleCancelClick}
                    onSave={this.handleSaveClick}
                />
            </Dialog>
        );
    }

    renderPerspectiveLink() {
        if (!isAdmin(this.props.profile)) {
            return;
        }

        const linkText = this.props.asUser ? "View as Admin" : "View as User";

        return (
            <ListItem
                leftIcon="cached"
                caption={linkText}
                onClick={this.props.onChangePerspective.bind(undefined, !this.props.asUser)}
                selectable
            />
        );
    }

    renderFavoritesApps() {
        const { organizations } = this.props;

        if (organizations && organizations.length === 0) {
            return;
        }

        return (
            <div id="nav_drawer_favorites_apps">
                <h5>Favorites Apps</h5>

                <FavoritesApps
                    apps={this.state.favoritesApps}
                    compact={true}
                    onNavClick={this.handleNavClick}
                />

                {this.state.favoritesApps.length < 3 &&
                    <footer>
                        <Button
                            theme={darkButton}
                            onClick={() => this.handleNavClick("/list")}
                            label="Add new from app page"
                            primary={true}
                            raised={true}
                        />
                    </footer>
                }
            </div>
        );
    }

    renderRecentApps() {
        const { organizations } = this.props;

        if (organizations && organizations.length === 0) {
            return;
        }

        return (
            <div id="nav_drawer_recent_apps">
                <h5>Recent Apps</h5>

                <RecentlyVisitedApps
                    apps={this.state.recentlyVisitedApps}
                    compact={true}
                    onNavClick={this.handleNavClick}
                />
            </div>
        );
    }

    renderMenuContent() {
        return (
            <>
                <div className={style.roleMenu}>{this.renderRoleMenu()}</div>
                <div className={style.favoritesApps}>{this.renderFavoritesApps()}</div>
                <div className={style.recentApps}>{this.renderRecentApps()}</div>
            </>
        );
    }

    renderRoleMenu() {
        const { profile, organizations } = this.props;

        if (isAdmin(profile)) {
            return <AdminMenu onNavClick={this.handleNavClick} />;
        }



        return <UserMenu organizations={organizations ? organizations.slice(0, 5) : []} onNavClick={this.handleNavClick} />;
    }

    render() {
        // eject if there is no user profile loaded yet
        if (!this.props.profile) {
            return <></>;
        }

        return (
            <>
                <Drawer active={this.props.isActive} onOverlayClick={this.props.onToggleClick} theme={theme}>
                    <div className={style.wrap}>
                        <div className={style.top}>
                            <IconButton className={style.menuToggle} icon="menu" onClick={this.props.onToggleClick} />
                        </div>

                        <div id="nav_drawer_menu_content" className={style.middle}>
                            {this.renderMenuContent()}
                        </div>

                        <div className={style.bottom}>
                            <List>
                                {this.renderPerspectiveLink()}

                                <ListItem
                                    leftIcon="router"
                                    caption="Status"
                                    onClick={this.handleStatusClick}
                                    selectable
                                />

                                <ListItem
                                    leftIcon="help"
                                    caption="Documentation"
                                    onClick={this.handleSupportClick}
                                    selectable
                                />
                            </List>
                        </div>
                    </div>
                </Drawer>

                {this.renderNewAppDialog()}
            </>
        );
    }
}